import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Maximize2, Ruler, Wrench } from 'lucide-react';
import { measureGuide } from '../../content/measureGuide';
import { Breadcrumbs, PageHero } from '../../components/shop/ShopBits';
import BacksetDiagram from '../../components/shop/BacksetDiagram';
import PhotoLightbox from '../../components/shop/PhotoLightbox';
import usePageMeta from '../../hooks/usePageMeta';

// /shop/how-to-measure — the one measurement every lock order turns on, shown door by door.
// Every "How to measure" link in the shop lands here (Hugh, 17 Sep 2026: it used to land on
// the enquiry form). A door with a photo shows the photo, enlargeable; the rest show a drawing.
const MeasurePage = () => {
  usePageMeta();
  const [open, setOpen] = useState(null);
  const photos = measureGuide.doors.filter((d) => d.img);
  const close = useCallback(() => setOpen(null), []);
  const move = useCallback((d) => setOpen((i) => (i === null ? null : Math.min(photos.length - 1, Math.max(0, i + d)))), [photos.length]);

  return (
    <div className="min-h-screen bg-white">
      <PageHero title={measureGuide.h1} sub={measureGuide.rule} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'How to measure' }]} />

        <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
          <BacksetDiagram kind="hinged" className="rounded-2xl border border-gray-200 bg-white p-4" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-red-600 mb-2">The one rule</p>
            <p className="text-2xl font-bold text-gray-900 leading-snug">Door open. Tape hooked on the edge. Read to the centre of the keyhole.</p>
            <ul className="mt-4 space-y-2 text-gray-700">
              <li className="flex items-start gap-2"><Ruler size={18} className="text-red-600 mt-0.5 flex-shrink-0" /> The lock on the door now is the one to measure — the new lock goes where it was.</li>
              <li className="flex items-start gap-2"><Wrench size={18} className="text-red-600 mt-0.5 flex-shrink-0" /> Not sure? Order with installation — the fit is checked before we book.</li>
            </ul>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your door</h2>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="measure-doors">
          {measureGuide.doors.map((d) => {
            const photoIndex = photos.indexOf(d);
            return (
              <li key={d.key} id={d.key} className="scroll-mt-24 rounded-2xl border border-gray-200 overflow-hidden bg-white flex flex-col" data-testid={`measure-${d.key}`}>
                {d.img ? (
                  <button type="button" onClick={() => setOpen(photoIndex)} className="group relative block w-full bg-gray-100 text-left" aria-label={`Enlarge: ${d.title}`}>
                    <img src={d.img} alt={d.imgAlt} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2 py-1 group-hover:bg-black/80"><Maximize2 size={12} /> Enlarge</span>
                  </button>
                ) : (
                  <BacksetDiagram kind={d.diagram} className="bg-white p-3 border-b border-gray-100" />
                )}
                <div className="p-4 flex-1">
                  <h3 className="font-bold text-gray-900">{d.title}</h3>
                  <dl className="mt-2 text-sm space-y-1.5">
                    <div><dt className="inline font-semibold text-gray-900">Tape: </dt><dd className="inline text-gray-700">{d.tape}</dd></div>
                    <div><dt className="inline font-semibold text-gray-900">Sizes: </dt><dd className="inline text-gray-700">{d.sizes}</dd></div>
                  </dl>
                  <p className="text-xs text-gray-600 mt-3">{d.tip}</p>
                </div>
              </li>
            );
          })}
        </ol>

        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Four ways it goes wrong</h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="measure-mistakes">
          {measureGuide.mistakes.map((m) => (
            <li key={m.title} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="font-semibold text-gray-900">{m.title}</p>
              <p className="text-sm text-gray-700 mt-1">{m.text}</p>
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-2xl border-2 border-red-200 bg-red-50/40 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-bold text-gray-900">Got the number? Keep it handy — or leave it to us; the fit is checked before we book.</p>
            <p className="text-sm text-gray-700 mt-1">Not sure which lock? A few taps about the door finds it.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/shop/find-my-lock" className="inline-flex items-center gap-2 rounded-full bg-red-600 text-white font-bold px-5 py-2.5 hover:bg-red-700">Find my lock <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>

      {open !== null && <PhotoLightbox items={photos.map((d) => ({ src: d.img, title: d.title, text: d.tape }))} index={open} onClose={close} onMove={move} label="Door" testId="measure-lightbox" />}
    </div>
  );
};

export default MeasurePage;
